import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, useTheme, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Chip, IconButton, Tooltip, alpha, Collapse, Avatar, Fade
} from '@mui/material';
import CellTowerIcon from '@mui/icons-material/CellTower';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import BugReportIcon from '@mui/icons-material/BugReport';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MapIcon from '@mui/icons-material/Map';
import StraightenIcon from '@mui/icons-material/Straighten';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { analyticsApi } from '../../services/api';

const CHART_COLORS = ['#7c4dff', '#b388ff', '#d1c4e9', '#ede7f6', '#9575cd', '#673ab7'];

// Tilt band colors keyed by classification (matches Slanting Analysis bands)
const BAND_COLORS = {
  essentially_vertical: '#4caf50',
  minor_tilt: '#8bc34a',
  notable_tilt: '#ff9800',
  significant_tilt: '#f44336',
};

const HighMastSummarySection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getUserOrganization } = useAuth();
  const organizationId = getUserOrganization()?.id;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Expandable table state
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [tableData, setTableData] = useState({});
  const [tableLoading, setTableLoading] = useState({});
  const [tablePagination, setTablePagination] = useState({});

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const response = await analyticsApi.getHighMastDashboardStats();
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching high mast stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Fetch detail table data
  const fetchTableData = useCallback(async (cardType, page = 0, rowsPerPage = 10) => {
    setTableLoading(prev => ({ ...prev, [cardType]: true }));
    try {
      const response = await analyticsApi.getHighMastDetails(cardType, {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      });
      setTableData(prev => ({ ...prev, [cardType]: response.data }));
    } catch (err) {
      console.error('Error fetching table data:', err);
    } finally {
      setTableLoading(prev => ({ ...prev, [cardType]: false }));
    }
  }, []);

  const handleExpandCard = (cardId, cardType) => {
    if (expandedCardId === cardId) {
      setExpandedCardId(null);
      return;
    }
    setExpandedCardId(cardId);
    if (!tablePagination[cardId]) {
      setTablePagination(prev => ({ ...prev, [cardId]: { page: 0, rowsPerPage: 10 } }));
    }
    if (!tableData[cardType]) {
      fetchTableData(cardType, 0, 10);
    }
  };

  const handlePageChange = (cardType, newPage) => {
    const rpp = tablePagination[expandedCardId]?.rowsPerPage || 10;
    setTablePagination(prev => ({ ...prev, [expandedCardId]: { page: newPage, rowsPerPage: rpp } }));
    fetchTableData(cardType, newPage, rpp);
  };

  const handleViewOnMap = (asset) => {
    if (asset.center_lat && asset.center_lng) {
      navigate('/map-view', {
        state: {
          selectedAsset: {
            id: asset.id,
            name: asset.name,
            code: asset.code,
            center_lat: asset.center_lat,
            center_lng: asset.center_lng,
          },
          flyTo: { lat: asset.center_lat, lng: asset.center_lng, zoom: 18 },
          source: 'assetsPage' // force Asset Mode (tab 0) so the High Mast marker renders
        }
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#7c4dff' }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (!stats) return null;

  // Prepare card data
  const inspected = stats.totalHighMasts - stats.highMastsWithoutInspection;
  const coveragePercent = stats.totalHighMasts > 0 ? Math.round((inspected / stats.totalHighMasts) * 100) : 0;

  const cards = [
    {
      id: 'total',
      cardType: 'total_high_masts',
      title: 'Total High Masts',
      count: stats.totalHighMasts,
      description: `${stats.subtypeBreakdown.map(s => `${s.count} ${s.subtype_name}`).join(', ') || 'No assets'}`,
      Icon: CellTowerIcon,
      color: '#7c4dff',
      chartData: stats.subtypeBreakdown.map((s, i) => ({ name: s.subtype_name, value: s.count, color: CHART_COLORS[i % CHART_COLORS.length] }))
    },
    {
      id: 'uninspected',
      cardType: 'uninspected',
      title: 'Uninspected',
      count: stats.highMastsWithoutInspection,
      description: `${coveragePercent}% coverage (${inspected}/${stats.totalHighMasts})`,
      Icon: AssignmentLateIcon,
      color: theme.palette.warning.main,
      chartData: [
        { name: 'Inspected', value: inspected, color: '#4caf50' },
        { name: 'Uninspected', value: stats.highMastsWithoutInspection, color: '#f44336' }
      ].filter(d => d.value > 0)
    },
    {
      id: 'defects',
      cardType: 'with_defects',
      title: 'Total Defects',
      count: stats.totalDefects,
      description: stats.defectsByType.length > 0 ? `${stats.defectsByType.length} defect types detected` : 'No defects recorded',
      Icon: BugReportIcon,
      color: theme.palette.error.main,
      chartData: stats.defectsByType.slice(0, 5).map((d, i) => ({ name: d.defect_type_name, value: d.count, color: CHART_COLORS[i % CHART_COLORS.length] }))
    },
    {
      id: 'top_asset',
      cardType: 'with_defects',
      title: 'Most Defective Asset',
      count: stats.topAssetsByDefects[0]?.defect_count || 0,
      description: stats.topAssetsByDefects[0]?.asset_code || 'No defects found',
      Icon: WarningIcon,
      color: theme.palette.secondary.main,
      chartData: null
    },
    (() => {
      const sl = stats.slanting || { analyzedCount: 0, alertsCount: 0, maxTilt: 0, byClassification: [] };
      const alertsPart = sl.alertsCount > 0 ? `${sl.alertsCount} alert${sl.alertsCount > 1 ? 's' : ''}` : '0 alerts';
      return {
        id: 'slanting',
        cardType: 'slanting',
        title: 'Slanting Status',
        count: sl.analyzedCount,
        description: sl.analyzedCount > 0
          ? `${sl.analyzedCount} analyzed · ${alertsPart} · max ${Number(sl.maxTilt).toFixed(1)}°`
          : 'No analyses yet',
        Icon: StraightenIcon,
        iconSx: { transform: 'rotate(90deg)' },
        color: '#7c4dff',
        chartData: (sl.byClassification || []).map(b => ({
          name: b.classification_label,
          value: b.count,
          color: BAND_COLORS[b.classification] || '#9e9e9e'
        }))
      };
    })()
  ];

  return (
    <Box>
      {/* KPI Cards Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
        gap: 1.5,
        mb: 3
      }}>
        {cards.map((card) => {
          const cardColor = card.color;
          const isExpanded = expandedCardId === card.id;
          const isHovered = hoveredCardId === card.id;
          const hasDonut = card.chartData && card.chartData.length > 0;
          return (
            <Paper
              key={card.id}
              onClick={() => handleExpandCard(card.id, card.cardType)}
              onMouseEnter={() => setHoveredCardId(card.id)}
              onMouseLeave={() => setHoveredCardId(null)}
              sx={{
                p: 0,
                borderRadius: 2,
                minHeight: '150px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
                cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
                }
              }}
            >
              {/* Header */}
              <Box sx={{
                px: 1.25, height: 46, flexShrink: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 0.5,
                borderBottom: '1px solid', borderColor: alpha(cardColor, 0.15), bgcolor: alpha(cardColor, 0.03)
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                  <Avatar sx={{ bgcolor: alpha(cardColor, 0.1), color: cardColor, width: 26, height: 26, mr: 1, flexShrink: 0 }}>
                    <card.Icon sx={{ fontSize: 15, ...(card.iconSx || {}) }} />
                  </Avatar>
                  <Tooltip title={card.title} placement="top-start">
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '0.78rem' }}>
                      {card.title}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>

              {/* Count + description */}
              <Box sx={{ px: 1.5, pt: 1.25, pb: 1, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <Typography component="div" sx={{
                  fontWeight: 800, fontSize: '2.75rem', lineHeight: 1.1, mb: 0.25,
                  backgroundImage: `linear-gradient(135deg, ${cardColor}, ${alpha(cardColor, 0.7)})`,
                  backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                }}>
                  {typeof card.count === 'string' ? card.count : (card.count || 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{
                  color: 'text.secondary', fontWeight: 500, fontSize: '0.68rem', lineHeight: 1.3,
                  minHeight: '2.6em', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden'
                }}>
                  {card.description}
                </Typography>

                {/* Donut + legend */}
                {hasDonut && (
                  <Box sx={{ mt: 1, height: 60, display: 'flex', alignItems: 'flex-start' }}>
                    <ResponsiveContainer width="40%" height="100%">
                      <PieChart>
                        <Pie data={card.chartData} cx="50%" cy="50%" innerRadius={12} outerRadius={24} paddingAngle={2} dataKey="value">
                          {card.chartData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip formatter={(value, name) => [`${value}`, name]}
                          contentStyle={{ fontSize: '0.7rem', padding: '4px 8px', border: `1px solid ${alpha(cardColor, 0.2)}`, borderRadius: 4 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ ml: 1, flex: 1, minWidth: 0 }}>
                      {card.chartData.slice(0, 3).map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.2 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: item.color, mr: 0.5, flexShrink: 0 }} />
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.6rem', flex: 1, minWidth: 0 }}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.6rem', ml: 0.5, flexShrink: 0 }}>
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                      {card.chartData.length > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', fontStyle: 'italic' }}>
                          +{card.chartData.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Expand button (revealed on hover / when expanded) */}
              <Fade in={isHovered || isExpanded}>
                <IconButton
                  onClick={(e) => { e.stopPropagation(); handleExpandCard(card.id, card.cardType); }}
                  size="small"
                  aria-expanded={isExpanded}
                  aria-label="show more"
                  sx={{
                    position: 'absolute', bottom: 6, right: 6,
                    bgcolor: alpha(theme.palette.background.paper, 0.9),
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
                    '&:hover': { bgcolor: theme.palette.background.paper }
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </Fade>
            </Paper>
          );
        })}
      </Box>

      {/* Expandable Detail Table */}
      {expandedCardId && (
        <Collapse in={!!expandedCardId}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {cards.find(c => c.id === expandedCardId)?.title} — Details
            </Typography>

            {tableLoading[cards.find(c => c.id === expandedCardId)?.cardType] ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                {cards.find(c => c.id === expandedCardId)?.cardType === 'slanting' ? (
                <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha('#7c4dff', 0.04) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Asset Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Max Tilt</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Classification</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Direction</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Analyzed</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const data = tableData['slanting']?.data || [];
                        if (data.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center' }}>
                                <Typography color="text.secondary">No slanting analyses yet</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return data.map((row) => {
                          const c = BAND_COLORS[row.classification] || '#9e9e9e';
                          return (
                            <TableRow key={row.id} hover sx={{ '& .MuiTableCell-root': { py: 1.2 } }}>
                              <TableCell>
                                <Chip label={row.code} size="small" sx={{ fontSize: '0.75rem', fontWeight: 600, bgcolor: alpha('#7c4dff', 0.1), color: '#7c4dff' }} />
                              </TableCell>
                              <TableCell><Typography variant="body2">{row.section || 'N/A'}</Typography></TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700, color: c }}>
                                  {row.tiltDegrees != null ? `${row.tiltDegrees.toFixed(2)}°` : '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip label={row.classificationLabel || '—'} size="small" sx={{ fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(c, 0.15), color: c }} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {row.tiltDirectionCompass
                                    ? `${row.tiltDirectionCompass}${row.tiltDirectionDegrees != null ? ` (${Math.round(row.tiltDirectionDegrees)}°)` : ''}`
                                    : (row.tiltDirectionDegrees != null ? `${Math.round(row.tiltDirectionDegrees)}°` : '—')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {row.analyzedAt ? new Date(row.analyzedAt).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="View on Map">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewOnMap(row); }}
                                    sx={{ color: '#7c4dff', bgcolor: alpha('#7c4dff', 0.08), '&:hover': { bgcolor: alpha('#7c4dff', 0.15) } }}>
                                    <MapIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
                ) : (
                <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha('#7c4dff', 0.04) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Asset Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Subtype</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Height</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Inspections</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Defects</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const cardType = cards.find(c => c.id === expandedCardId)?.cardType;
                        const data = tableData[cardType]?.data || [];
                        if (data.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={8} sx={{ py: 4, textAlign: 'center' }}>
                                <Typography color="text.secondary">No data available</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return data.map((row) => (
                          <TableRow key={row.id} hover sx={{ '& .MuiTableCell-root': { py: 1.2 } }}>
                            <TableCell>
                              <Chip label={row.code} size="small" sx={{ fontSize: '0.75rem', fontWeight: 600, bgcolor: alpha('#7c4dff', 0.1), color: '#7c4dff' }} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.name}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.subtype}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.section || 'N/A'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.height ? `${row.height}m` : '—'}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={row.inspections?.count || 0} size="small"
                                sx={{ fontSize: '0.75rem', bgcolor: row.inspections?.count > 0 ? alpha('#4caf50', 0.1) : alpha('#f44336', 0.1), color: row.inspections?.count > 0 ? '#4caf50' : '#f44336' }} />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={row.defectCount || 0} size="small"
                                sx={{ fontSize: '0.75rem', bgcolor: row.defectCount > 0 ? alpha('#f44336', 0.1) : alpha('#4caf50', 0.1), color: row.defectCount > 0 ? '#f44336' : '#4caf50' }} />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="View on Map">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewOnMap(row); }}
                                  sx={{ color: '#7c4dff', bgcolor: alpha('#7c4dff', 0.08), '&:hover': { bgcolor: alpha('#7c4dff', 0.15) } }}>
                                  <MapIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}
                {(() => {
                  const cardType = cards.find(c => c.id === expandedCardId)?.cardType;
                  const pagination = tableData[cardType]?.pagination;
                  if (!pagination) return null;
                  return (
                    <TablePagination
                      component="div"
                      count={pagination.total}
                      page={tablePagination[expandedCardId]?.page || 0}
                      rowsPerPage={tablePagination[expandedCardId]?.rowsPerPage || 10}
                      onPageChange={(e, newPage) => handlePageChange(cardType, newPage)}
                      rowsPerPageOptions={[10]}
                    />
                  );
                })()}
              </>
            )}
          </Paper>
        </Collapse>
      )}
    </Box>
  );
};

export default HighMastSummarySection;
